import React, { useState, useEffect, useRef } from "react";
import maskDate from "../utils/maskDate";
import CustomButton from "./CustomButton";
import { isSameDay, isSameMonth } from "date-fns";

const MonthRangePicker = ({
  anchor="bottom", // Position of the datepicker
  type=0,
  placeholderStart = "入社月",
  placeholderEnd = "退社月",
  startMonthDisabledTime = "",
  startMonth = "", // YYYY-MM-DD
  endMonth = "",  // YYYY-MM-DD
  onChange,
  disabled = false,
}: any) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activePicker, setActivePicker] = useState<"start" | "end" | null>(null);
  const datepickerRef = useRef<HTMLDivElement | null>(null);

  const renderMonths = () => {
    const year = currentDate.getFullYear();
    const months = [
      "1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月",
    ];

    return months.map((month, index) => {
      const day = new Date(Date.UTC(year, index));
      const dayString = day.toISOString().split('T')[0];
      let className = "font-normal flex items-center justify-center text-sm px-4 py-2 rounded text-[#000] dark:text-dark-6 w-[5rem] h-10";

      const isActiveSelected =
        (activePicker === 'start' && startMonth === dayString) ||
        (activePicker === 'end' && endMonth === dayString);

      let isDisabled = false;
      if (activePicker === 'start') {
        isDisabled = new Date(dayString) < new Date(startMonthDisabledTime) || new Date(dayString) > new Date(endMonth);
      } else if (activePicker === 'end') {
        isDisabled = new Date(dayString) < new Date(startMonth);
        if(startMonthDisabledTime) {
          isDisabled = new Date(dayString) < new Date(startMonthDisabledTime) || new Date(dayString) < new Date(startMonth);
        }
      }

      if (isActiveSelected) {
        className += " border border-2 border-[#FF861A] cursor-pointer";
      } else if (!isDisabled) {
        className += " hover:bg-[#FFF3E5] cursor-pointer";
      }

      if (isDisabled) {
        className += " opacity-50 cursor-default";
      }

      return (
        <div
          key={index}
          className={className}
          onClick={() => !isDisabled && handleMonthClick(dayString)}
        >
          {month}
        </div>
      );
    });
  };

  const handleMonthClick = (selectedMonth: string) => {
    if (activePicker === 'start') {
      !isSameMonth(selectedMonth, startMonth) ? onChange(selectedMonth, endMonth) : onChange(null, endMonth);
    } else if (activePicker === 'end') {
      !isSameMonth(selectedMonth, endMonth) ? onChange(startMonth, selectedMonth) : onChange(startMonth, null);
    }
    setActivePicker(null);
  };

  const updateInput = (month) => {
    if (month) {
      let monthMask: any = month;
      if (type !== 0) monthMask = maskDate(month, type);
      return monthMask;
    }
    return "";
  };

  const openStartPicker = () => {
    const initialDate = startMonth ? new Date(startMonth) : new Date();
    setCurrentDate(initialDate);
    setActivePicker('start');
  };

  const openEndPicker = () => {
    const initialDate = endMonth ? new Date(endMonth) : new Date();
    setCurrentDate(initialDate);
    setActivePicker('end');
  };

  const handleDocumentClick = (e: MouseEvent) => {
    if (datepickerRef.current && !datepickerRef.current.contains(e.target as Node)) {
      setActivePicker(null);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [startMonth, endMonth]);

  return (
    <div className="w-full">
      <div className="relative" ref={datepickerRef}>
        <div className="flex gap-2 items-center">
          <div className={`flex-1 relative flex items-center group rounded hover:bg-[#FFF3E5] ${activePicker === 'start' ? "outline outline-1 outline-[#FF861A]" : ""}`}>
            <input
              type="text"
              placeholder={placeholderStart}
              className={`h-9 ${!disabled ? "hover:cursor-pointer bg-white group-hover:bg-[#FFF3E5] group-hover:border-transparent alu-text-base-2" : "text-[#BBB] bg-[#F7F7F7] hover:cursor-default"} text-sm md:text-sm w-full rounded border border-stroke py-2 pl-3 pr-3 sm:pr-8 outline-none transition focus:border-primary dark:border-dark-3 dark:text-dark-6 dark:focus:border-primary`}
              value={updateInput(startMonth)}
              onClick={() => !disabled && openStartPicker()}
              readOnly
            />
            <span
              className={`absolute right-2 ${!disabled ? "cursor-pointer text-[#808080] group-hover:text-[#FF861A]" : "cursor-default text-[#BBB]"}`}
              onClick={() => !disabled && openStartPicker()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <g clipPath="url(#clip0_10950_2245)">
                  <path
                    d="M16.59 8.58997L12 13.17L7.41 8.58997L6 9.99997L12 16L18 9.99997L16.59 8.58997Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_10950_2245">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </span>
          </div>
          <span className="">-</span>
          <div className={`flex-1 relative flex items-center group rounded hover:bg-[#FFF3E5] ${activePicker === 'end' ? "outline outline-1 outline-[#FF861A]" : ""}`}>
            <input
              type="text"
              placeholder={placeholderEnd}
              className={`h-9 ${!disabled ? "hover:cursor-pointer bg-white group-hover:bg-[#FFF3E5] group-hover:border-transparent alu-text-base-2" : "text-[#BBB] bg-[#F7F7F7] hover:cursor-default"} text-sm md:text-sm w-full rounded border border-stroke py-2 pl-3 pr-3 sm:pr-8  outline-none transition focus:border-primary dark:border-dark-3 dark:text-dark-6 dark:focus:border-primary`}
              value={updateInput(endMonth)}
              onClick={() => !disabled && openEndPicker()}
              readOnly
            />
            <span
              className={`absolute right-2 ${!disabled ? "cursor-pointer text-[#808080] group-hover:text-[#FF861A]" : "cursor-default text-[#BBB]"}`}
              onClick={() => !disabled && openEndPicker()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <g clipPath="url(#clip0_10950_2245)">
                  <path
                    d="M16.59 8.58997L12 13.17L7.41 8.58997L6 9.99997L12 16L18 9.99997L16.59 8.58997Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_10950_2245">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </span>
          </div>
        </div>

        {activePicker && (
          <div className={`min-w-[19rem] absolute ${anchor === "bottom" ? "top-full mt-2 shadow-2xl" : "bottom-full mb-2"} left-0 z-10 rounded-2xl alu-panel-box-shadow bg-white dark:border-dark-3 dark:bg-dark-3`}>
            <div className="flex items-end justify-center gap-6 px-4 pt-6 py-4">
              <button
                className="rounded-full hover:bg-[#FFF3E5] text-dark dark:text-white dark:hover:bg-dark disabled:bg-transparent"
                onClick={() =>
                  setCurrentDate(new Date(currentDate.setFullYear(currentDate.getFullYear() - 1)))
                }
              >
                <svg className="hover:fill-[#343A3D] fill-[#808080]" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15.4102 16.59L10.8302 12L15.4102 7.41L14.0002 6L8.00016 12L14.0002 18L15.4102 16.59Z"/>
                </svg>
              </button>

              <div className="text-sm text-dark-3 dark:text-white alu-text-base-2 flex items-center">
                {currentDate.getFullYear()}年
              </div>

              <button
                className="rounded-full text-dark hover:bg-[#FFF3E5] dark:text-white dark:hover:bg-dark disabled:bg-transparent"
                onClick={() =>
                  setCurrentDate(new Date(currentDate.setFullYear(currentDate.getFullYear() + 1)))
                }
              >
                <svg className="hover:fill-[#343A3D] fill-[#808080]" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M8.58984 7.41L13.1698 12L8.58984 16.59L9.99984 18L15.9998 12L9.99984 6L8.58984 7.41Z"/>
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-3 px-6 pb-6 gap-2">
              {renderMonths()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthRangePicker;